import { AccForm, AccFormSection } from 'app/api/forms/dto/accFormDto'
import { FC, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { SectionItem } from '../legal/components/section'
import { SideBar } from './components/SideBar'
import { ReviewSection } from './components/ReviewSections'
import {
    ComponentHeights,
    PrimaryButton,
    Scroll,
    SecondaryButton,
    Text,
    Loading,
    LottieAnimationName,
} from '@tuhi/components'
import { media } from '@tuhi/components/src/theme/breakpoints'
import { useNavigate, useLocation } from 'react-router-dom'
import { createFormCopy } from 'app/api/forms/repository/formsRepository'
import { logger } from 'app/logging/Logger'
import { useSelector, useDispatch } from 'react-redux'
import { selectAuthState } from 'app/store/selectors'
import { openModal, closeModal } from 'app/store/slices/modalSlice'
import {
    useQuestionnaireService,
    QuestionnaireParser,
} from 'app/hooks/useQuestionnaireService'
import { useFormRepository } from 'app/hooks/useFormRepository'
import { resolveFormDisplayValues } from 'app/api/forms/utils/questionnaireResponseUtils'

const Container = styled.div`
    display: flex;
    flex-direction: row;
    height: 100%;

    ${media.mobile} {
        flex-direction: column;
    }
`

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 24px 0px;
    gap: 24px;
`

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const QuestionnaireHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 64px;
    padding: 16px 0;
    border-bottom: 1px solid ${(p) => p.theme.colors.tertiary.tertiary200};
    margin-bottom: 24px;

    ${media.mobile} {
        flex-direction: column;
        gap: 16px;
        align-items: flex-start;
    }
`

const QuestionnaireColumn = styled.div`
    display: flex;
    gap: 24px;

    ${media.mobile} {
        flex-direction: column;
        gap: 12px;
        width: 100%;
    }
`

const QuestionnaireField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const ScrollContainer = styled(Scroll)`
    padding: 0px 24px;

    ${media.mobile} {
        padding: 0px 16px;
    }
`

const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 16px;
    padding-left: 24px;

    ${media.mobile} {
        padding-left: 0;
        flex-direction: column;
        width: 100%;
    }
`

interface Props {
    form: AccForm
    strippedFields?: string[]
    onReady?: () => void
}

interface Section {
    anchor: SectionItem
    content: AccFormSection
}

/**
 * Walk all fields in the form and return the linkIds of required fields
 * that have empty content.
 * @deprecated Validation removed — forms are submitted without required-field checks.
 */

export const FormReview: FC<Props> = ({
    form,
    strippedFields = [],
    onReady,
}) => {
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    const { currentUser } = useSelector(selectAuthState)
    const { loadQuestionnaireWithValueSets, prefetchValueSets } =
        useQuestionnaireService()
    const {
        submitFormToFHIR,
        updateForm,
        markFormEnteredInError,
        fetchQuestionnaireByName,
    } = useFormRepository()
    const [activeId, setActiveId] = useState('')
    const [sections, setSections] = useState<Section[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isInErrorCorrectionMode, setIsInErrorCorrectionMode] =
        useState(false)
    const [originalFormId, setOriginalFormId] = useState<string | null>(null)
    const [questionnaireMetadata, setQuestionnaireMetadata] = useState<{
        title: string
        publisher: string
    } | null>(null)
    const scrollingRef = useRef(false)
    const [resolvedForm, setResolvedForm] = useState<AccForm>(form)
    const [, setQuestionnaireParser] = useState<QuestionnaireParser | null>(
        null
    )
    const componentMountRef = useRef(performance.now())
    const renderCountRef = useRef(0)
    const metadataReadyRef = useRef(false)
    const sectionsReadyRef = useRef(false)
    const onReadyFiredRef = useRef(false)

    // Get the return path from navigation state, default to forms list
    const returnTo =
        (location.state as { returnTo?: string })?.returnTo || '/forms'

    // Mount/unmount logging
    useEffect(() => {
        logger.info('[perf][form-review] Mounted', {
            action: 'perf_form_review_mount',
            metadata: {
                formId: form.id,
                formType: form.type,
                sectionCount: form.sections?.length,
            },
        })
        return () => {
            logger.info('[perf][form-review] Unmounted', {
                action: 'perf_form_review_unmount',
                metadata: { formId: form.id },
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Mount/unmount timing log per form: must not fire again when the form's type or section count changes
    }, [form.id])

    const fireOnReady = () => {
        if (
            metadataReadyRef.current &&
            sectionsReadyRef.current &&
            !onReadyFiredRef.current
        ) {
            onReadyFiredRef.current = true
            logger.info(
                '[perf][form-review] onReady fired — sections + metadata both ready',
                {
                    action: 'perf_form_review_on_ready',
                    metadata: {
                        formId: form.id,
                        msSinceMount: Math.round(
                            performance.now() - componentMountRef.current
                        ),
                    },
                }
            )
            onReady?.()
        }
    }

    // Debug logging
    useEffect(() => {
        logger.debug('FormReview navigation state', {
            action: 'form_review_nav_state',
            metadata: {
                hasState: !!location.state,
                returnTo,
                fullState: location.state,
            },
        })
    }, [location.state, returnTo])

    // Fetch questionnaire metadata
    useEffect(() => {
        const loadQuestionnaireMetadata = async () => {
            const fetchStart = performance.now()
            logger.info('[perf][form-review] Fetching questionnaire metadata', {
                action: 'perf_fetch_questionnaire_metadata_start',
                metadata: {
                    formType: form.type,
                    msSinceMount: Math.round(
                        fetchStart - componentMountRef.current
                    ),
                },
            })
            try {
                const metadata = await fetchQuestionnaireByName(form.type)
                const fetchMs = performance.now() - fetchStart
                logger.info(
                    '[perf][form-review] Questionnaire metadata received',
                    {
                        action: 'perf_fetch_questionnaire_metadata_done',
                        metadata: {
                            formType: form.type,
                            hasMetadata: !!metadata,
                            title: metadata?.title,
                            fetchMs: Math.round(fetchMs),
                            msSinceMount: Math.round(
                                performance.now() - componentMountRef.current
                            ),
                        },
                    }
                )
                if (metadata) {
                    setQuestionnaireMetadata(metadata)
                }
                metadataReadyRef.current = true
                fireOnReady()
            } catch (error) {
                logger.error(
                    'Failed to load questionnaire metadata',
                    error as Error,
                    {
                        action: 'load_questionnaire_metadata_error',
                        metadata: { formType: form.type },
                    }
                )
                metadataReadyRef.current = true
                fireOnReady()
            }
        }

        loadQuestionnaireMetadata()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fireOnReady is rebuilt every render and only reads refs; re-running this fetch on it would re-request the questionnaire metadata
    }, [form.type, fetchQuestionnaireByName])

    // Resolve display values from ValueSets
    useEffect(() => {
        let cancelled = false
        const resolveStart = performance.now()

        const resolveDisplayValues = async () => {
            if (!currentUser?.sub) {
                logger.debug('No currentUser.sub, skipping resolution', {
                    action: 'resolve_skip_no_user',
                })
                return
            }

            logger.info('[perf][form-review] Starting ValueSet resolution', {
                action: 'perf_resolve_start',
                metadata: {
                    formType: form.type,
                    msSinceMount: Math.round(
                        resolveStart - componentMountRef.current
                    ),
                },
            })

            try {
                const parserStart = performance.now()
                const parser = await loadQuestionnaireWithValueSets(
                    form.type,
                    currentUser.sub
                )
                const parserMs = performance.now() - parserStart

                logger.info('[perf][form-review] Parser loaded', {
                    action: 'perf_resolve_parser_loaded',
                    metadata: {
                        hasParser: !!parser,
                        parserMs: Math.round(parserMs),
                        msSinceMount: Math.round(
                            performance.now() - componentMountRef.current
                        ),
                    },
                })

                if (cancelled || !parser) {
                    setResolvedForm(form)
                    return
                }

                // Store the parser for required-field validation
                setQuestionnaireParser(parser)

                const valueSetUrls = parser.getValueSetUrls()
                logger.info('[perf][form-review] ValueSet URLs retrieved', {
                    action: 'perf_resolve_valueset_urls',
                    metadata: {
                        urlCount: valueSetUrls.length,
                        msSinceMount: Math.round(
                            performance.now() - componentMountRef.current
                        ),
                    },
                })

                if (valueSetUrls.length === 0) {
                    // loadQuestionnaireWithValueSets already attempted a
                    // direct GraphQL fallback when DataStore returned stale
                    // data. If we still have 0 URLs, accept it as the final
                    // result — the questionnaire genuinely has no ValueSets.
                    setResolvedForm(form)
                    return
                }

                const prefetchStart = performance.now()
                const valueSets = await prefetchValueSets(
                    valueSetUrls,
                    currentUser.sub
                )
                if (cancelled) return
                const prefetchMs = performance.now() - prefetchStart

                logger.info('[perf][form-review] ValueSets fetched', {
                    action: 'perf_resolve_valuesets_fetched',
                    metadata: {
                        mapSize: valueSets.size,
                        prefetchMs: Math.round(prefetchMs),
                        msSinceMount: Math.round(
                            performance.now() - componentMountRef.current
                        ),
                    },
                })

                const resolved = resolveFormDisplayValues(
                    form,
                    parser,
                    valueSets
                )

                // Count what changed
                let changedCount = 0
                resolved.sections.forEach((section, si) => {
                    section.rows.forEach((row, ri) => {
                        row.columns.forEach((colGroup, ci) => {
                            colGroup.forEach((field, fi) => {
                                const origField =
                                    form.sections[si]?.rows[ri]?.columns[ci]?.[
                                        fi
                                    ]
                                if (
                                    origField &&
                                    field.content !== origField.content
                                ) {
                                    changedCount++
                                }
                            })
                        })
                    })
                })
                logger.info(
                    '[perf][form-review] ValueSet resolution complete',
                    {
                        action: 'perf_resolve_complete',
                        metadata: {
                            changedCount,
                            totalResolveMs: Math.round(
                                performance.now() - resolveStart
                            ),
                            msSinceMount: Math.round(
                                performance.now() - componentMountRef.current
                            ),
                        },
                    }
                )

                setResolvedForm(resolved)
            } catch {
                logger.warn('Failed to resolve ValueSet display values', {
                    action: 'resolve_display_values_error',
                    metadata: { formType: form.type },
                })
                setResolvedForm(form)
            }
        }

        resolveDisplayValues()

        return () => {
            cancelled = true
        }
    }, [
        form,
        currentUser?.sub,
        loadQuestionnaireWithValueSets,
        prefetchValueSets,
    ])

    // Re-evaluate required-field validation whenever the form or parser changes
    // (validation display removed — forms submit without required-field checks)

    const handleSectionUpdate = async (updatedSection: AccFormSection) => {
        logger.debug('Updating section in form', {
            action: 'update_section',
            metadata: { sectionTitle: updatedSection.title, formId: form.id },
        })

        // Create updated form with the edited section
        const updatedForm: AccForm = {
            ...form,
            sections: form.sections.map((s) =>
                s.title === updatedSection.title ? updatedSection : s
            ),
        }

        await updateForm(form.id, updatedForm)
    }

    const handleCancel = () => {
        // If in error correction mode, just clear the flag and navigate back
        // No form was created in DynamoDB, so nothing to delete
        if (isInErrorCorrectionMode) {
            logger.debug('Canceling error correction mode', {
                action: 'cancel_error_correction',
                metadata: { originalFormId: form.id },
            })
        }

        navigate(returnTo)
    }

    const handleEnteredInError = () => {
        // Enable error correction mode - don't create a copy yet
        // Just allow editing of the current form
        setIsInErrorCorrectionMode(true)
        setOriginalFormId(form.id)

        logger.debug('Entered in error mode activated', {
            action: 'entered_in_error_mode',
            metadata: { originalFormId: form.id },
        })
    }

    const handleSubmit = async () => {
        if (!currentUser) {
            logger.error('No current user found', new Error('Missing user'), {
                action: 'submit_form_no_user',
            })
            alert('Unable to submit form. Please log in again.')
            return
        }

        try {
            setIsSubmitting(true)

            dispatch(
                openModal({
                    children: (
                        <Loading
                            name={LottieAnimationName.FileTransfer}
                            width="200px"
                            message="Submitting form to FHIR server..."
                        />
                    ),
                })
            )

            // If in error correction mode, create a new form with the edits
            let formIdToSubmit = form.id

            if (isInErrorCorrectionMode && originalFormId) {
                logger.debug('Creating corrected form copy', {
                    action: 'create_corrected_form',
                    metadata: { originalFormId },
                })

                // Create a new form with status "Created" (not "CREATED")
                const newFormId = await createFormCopy(originalFormId)
                formIdToSubmit = newFormId

                logger.debug('Corrected form created', {
                    action: 'corrected_form_created',
                    metadata: { originalFormId, newFormId },
                })

                // Mark the original form as "Entered in error" in FHIR
                logger.debug(
                    'Marking original form as entered in error in FHIR - BEFORE CALL',
                    {
                        action: 'mark_original_error_fhir_before',
                        metadata: {
                            originalFormId,
                            originalFormIdType: typeof originalFormId,
                            originalFormIdLength: originalFormId?.length,
                            userId: currentUser.id,
                        },
                    }
                )

                await markFormEnteredInError(originalFormId, currentUser.id)

                logger.debug(
                    'Marking original form as entered in error in FHIR - AFTER CALL',
                    {
                        action: 'mark_original_error_fhir_after',
                        metadata: { originalFormId },
                    }
                )
            }

            logger.debug('Submitting form to FHIR', {
                action: 'submit_form_start',
                metadata: {
                    formId: formIdToSubmit,
                    userId: currentUser.id,
                    isErrorCorrection: isInErrorCorrectionMode,
                    originalFormId,
                },
            })

            const success = await submitFormToFHIR(
                formIdToSubmit,
                currentUser.id
            )

            dispatch(closeModal())

            if (success) {
                logger.debug('Form submitted successfully', {
                    action: 'submit_form_success',
                    metadata: {
                        formId: formIdToSubmit,
                        userId: currentUser.id,
                        originalFormId,
                    },
                })

                // Navigate to return path on success
                navigate(returnTo)
            } else {
                logger.warn('Form submission returned false', {
                    action: 'submit_form_failed',
                    metadata: {
                        formId: formIdToSubmit,
                        userId: currentUser.id,
                    },
                })

                alert('Form submission failed. Please try again.')
            }
        } catch (error) {
            const err =
                error instanceof Error ? error : new Error('Unknown error')
            logger.error('Error submitting form to FHIR', err, {
                action: 'submit_form_error',
                metadata: { formId: form.id, error: err.message },
            })

            dispatch(closeModal())

            alert(
                'An error occurred while submitting the form. Please try again.'
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    useEffect(() => {
        const sections = resolvedForm.sections.map((section) => {
            return {
                anchor: {
                    title: section.title,
                    id: section.title.toLowerCase().replace(' ', ''),
                },
                content: section,
            }
        })

        logger.info('[perf][form-review] Sections built from resolvedForm', {
            action: 'perf_sections_built',
            metadata: {
                sectionCount: sections.length,
                isResolvedSameAsForm: resolvedForm === form,
                msSinceMount: Math.round(
                    performance.now() - componentMountRef.current
                ),
            },
        })

        setSections(sections)
        setActiveId(sections[0].anchor.id)
        sectionsReadyRef.current = true
        fireOnReady()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Sections are rebuilt from resolvedForm only: re-running on form or on fireOnReady (new every render) would reset the active anchor
    }, [resolvedForm])

    // Set anchor to active as user scrolls
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (scrollingRef.current) return // 👈 Skip updates during manual scroll

                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort(
                        (a, b) =>
                            a.boundingClientRect.top - b.boundingClientRect.top
                    )

                if (visible.length > 0) {
                    const topSection = visible[0]
                    setActiveId(topSection.target.id)
                }
            },
            {
                root: null,
                rootMargin: '0px 0px -90% 0px',
                threshold: 0,
            }
        )

        sections.forEach(({ anchor }) => {
            const el = document.getElementById(anchor.id)
            if (el) observer.observe(el)
        })

        return () => observer.disconnect()
    }, [sections])

    return (
        <Container>
            {(() => {
                renderCountRef.current++
                logger.info('[perf][form-review] Render', {
                    action: 'perf_form_review_render',
                    metadata: {
                        formId: form.id,
                        renderNumber: renderCountRef.current,
                        hasSections: sections.length > 0,
                        sectionCount: sections.length,
                        hasQuestionnaireMetadata: !!questionnaireMetadata,
                        isResolvedSameAsForm: resolvedForm === form,
                        msSinceMount: Math.round(
                            performance.now() - componentMountRef.current
                        ),
                    },
                })
                return null
            })()}

            <SideBar
                anchors={sections.flatMap((section) => section.anchor)}
                activeId={activeId}
                setActiveId={setActiveId}
                scrollingRef={scrollingRef}
            />

            <ScrollContainer
                maxHeight="100vh"
                offsets={[ComponentHeights.AuthenticatedHeader]}
                verticalScrollBarVerticalPadding={24}
                verticalScrollBarLeftPadding={16}
            >
                <Content>
                    <Header>
                        {questionnaireMetadata && (
                            <QuestionnaireHeader>
                                <QuestionnaireColumn>
                                    <QuestionnaireField>
                                        <Text
                                            size="xSmall"
                                            $weight="medium"
                                            color="secondary"
                                        >
                                            Title
                                        </Text>
                                        <Text
                                            size="xSmall"
                                            $weight="medium"
                                            color="primary"
                                        >
                                            {questionnaireMetadata.title}
                                        </Text>
                                    </QuestionnaireField>
                                    <QuestionnaireField>
                                        <Text
                                            size="xSmall"
                                            $weight="medium"
                                            color="secondary"
                                        >
                                            Publisher
                                        </Text>
                                        <Text
                                            size="xSmall"
                                            $weight="medium"
                                            color="primary"
                                        >
                                            {questionnaireMetadata.publisher}
                                        </Text>
                                    </QuestionnaireField>
                                </QuestionnaireColumn>
                                {form.status === 'Complete' &&
                                    !isInErrorCorrectionMode && (
                                        <SecondaryButton
                                            $type="negative"
                                            width="small"
                                            height="small"
                                            onClick={handleEnteredInError}
                                        >
                                            Entered in error
                                        </SecondaryButton>
                                    )}
                            </QuestionnaireHeader>
                        )}
                        {(form.status !== 'Complete' ||
                            isInErrorCorrectionMode) && (
                            <>
                                <Text size="large" $weight="bold">
                                    Review and edit
                                </Text>
                                <Text size="small" $weight="regular">
                                    Make sure all all the information is
                                    accurate and true before submitting.
                                </Text>
                            </>
                        )}
                    </Header>

                    {sections.map((section) => {
                        return (
                            <ReviewSection
                                id={section.anchor.id}
                                key={section.anchor.id}
                                form={form}
                                section={section.content}
                                onSectionUpdated={handleSectionUpdate}
                                isInErrorCorrectionMode={
                                    isInErrorCorrectionMode
                                }
                                emptyRequiredFields={[]}
                                strippedFields={strippedFields}
                            />
                        )
                    })}

                    {(form.status !== 'Complete' ||
                        isInErrorCorrectionMode) && (
                        <ButtonsContainer>
                            <SecondaryButton
                                width="small"
                                height="small"
                                onClick={handleCancel}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </SecondaryButton>
                            <PrimaryButton
                                width="small"
                                height="small"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                Submit
                            </PrimaryButton>
                        </ButtonsContainer>
                    )}
                </Content>
            </ScrollContainer>
        </Container>
    )
}
